import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import './StudentDashboard.css'
import '../styles/dashboard-header.css'
import LoadingScreen from '../components/LoadingScreen'

const StudentDashboard = () => {
  const [user, setUser] = useState(null)
  const [dashboardData, setDashboardData] = useState(null)
  const [availableTestsCount, setAvailableTestsCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const history = useHistory()

  useEffect(() => {
    const token = localStorage.getItem('access_token')
    if (!token) {
      history.push('/login')
      return
    }

    const userData = JSON.parse(localStorage.getItem('user') || '{}')
    if (userData.role !== 'student') {
      // Redirect to appropriate dashboard
      if (userData.role === 'admin') {
        history.push('/admin-dashboard')
      } else if (userData.role === 'teacher') {
        history.push('/teacher-dashboard')
      } else if (userData.role === 'invigilator') {
        history.push('/invigilator-dashboard')
      } else if (userData.role === 'volunteer') {
        history.push('/volunteer-dashboard')
      }
      return
    }

    setUser(userData)
    fetchDashboardData()
    fetchAvailableTests()
  }, [history])

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('access_token')
      const response = await fetch('/api/student/dashboard', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        setDashboardData(data)
      } else {
        setError('Failed to load dashboard data')
      }
    } catch (err) {
      setError('Network error')
    } finally {
      setLoading(false)
    }
  }

  const fetchAvailableTests = async () => {
    try {
      const token = localStorage.getItem('access_token')
      const response = await fetch('/api/student/available-tests', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        setAvailableTestsCount(data.tests?.length || 0)
      }
    } catch (err) {
      console.error('Failed to fetch available tests count')
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('user')
    history.push('/login')
  }

  const handleTakeTest = (testId) => {
    history.push(`/test-taking/${testId}`)
  }

  if (loading) {
    return <LoadingScreen message="Loading Student Dashboard" />
  }

  if (error) {
    return (
      <div className="dashboard-container">
        <div className="error">{error}</div>
      </div>
    )
  }

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div className="header-content">
          <h1>Student Dashboard</h1>
          <div className="user-info">
            <span>Welcome, {user?.full_name}</span>
            <button onClick={handleLogout} className="logout-button">
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="dashboard-main">
        {dashboardData?.profile && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: '#fff', border: '1px solid #e3e4f0', borderRadius: '12px', padding: '14px 18px', marginBottom: '20px' }}>
            <span style={{ fontSize: '22px' }}>{dashboardData.profile.is_active ? '✅' : '⏳'}</span>
            <div>
              <div style={{ fontWeight: 700, color: '#1a1a26' }}>{dashboardData.profile.full_name}</div>
              <div style={{ color: '#6c6c7a', fontSize: '0.9rem' }}>{dashboardData.profile.email} · {dashboardData.profile.role}</div>
              <div style={{ fontSize: '0.9rem', marginTop: '2px' }}>
                Account status: <strong style={{ color: dashboardData.profile.is_active ? '#15803d' : '#b45309' }}>
                  {dashboardData.profile.is_active ? 'Active' : (dashboardData.profile.rejection_status === 'rejected' ? 'Rejected' : 'Pending approval')}
                </strong>
              </div>
            </div>
          </div>
        )}

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">📊</div>
            <div className="stat-content">
              <h3>{availableTestsCount}</h3>
              <p>Available Tests</p>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">✅</div>
            <div className="stat-content">
              <h3>{dashboardData?.statistics?.passed_attempts || 0}</h3>
              <p>Passed Tests</p>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">📈</div>
            <div className="stat-content">
              <h3>{dashboardData?.statistics?.average_score || 0}%</h3>
              <p>Average Score</p>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">🏆</div>
            <div className="stat-content">
              <h3>{dashboardData?.statistics?.certificates_earned || 0}</h3>
              <p>Certificates</p>
            </div>
          </div>

          <div className="stat-card" style={{ cursor: 'pointer' }} onClick={() => history.push('/my-payments')}>
            <div className="stat-icon">🧾</div>
            <div className="stat-content">
              <h3>{dashboardData?.payments?.pending || 0}</h3>
              <p>Fees Due</p>
            </div>
          </div>
        </div>

        <div className="dashboard-sections">
          <section className="recent-tests">
            <h2>Recent Test Attempts</h2>
            <div className="tests-list">
              {dashboardData?.recent_attempts?.map((attempt) => (
                <div key={attempt.id} className="test-item">
                  <div className="test-info">
                    <h4>{attempt.test_title}</h4>
                    <p>Score: {attempt.percentage}%</p>
                    <p>Status: {attempt.status}</p>
                  </div>
                  <div className="test-date">
                    {new Date(attempt.created_at).toLocaleDateString()}
                  </div>
                </div>
              )) || <p>No recent attempts</p>}
            </div>
          </section>

          <section className="certificates">
            <h2>Certificates</h2>
            <div className="certificates-list">
              {dashboardData?.certificates?.map((cert) => (
                <div key={cert.id} className="certificate-item">
                  <div className="cert-info">
                    <h4>{cert.test_title}</h4>
                    <p>Issued: {new Date(cert.issued_date).toLocaleDateString()}</p>
                  </div>
                  <button className="download-button">
                    Download
                  </button>
                </div>
              )) || <p>No certificates yet</p>}
            </div>
          </section>
        </div>

        <div className="action-buttons">
          <button 
            className="primary-button"
            onClick={() => history.push('/available-tests')}
          >
            View Available Tests
          </button>
          <button
            className="secondary-button"
            onClick={() => history.push('/test-history')}
          >
            View Test History
          </button>
          <button
            className="secondary-button"
            onClick={() => history.push('/my-payments')}
          >
            My Payments
          </button>
        </div>
      </main>
    </div>
  )
}

export default StudentDashboard
